/*
 * Copyright 2026 Sony Corporation
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import type { AppConfig } from '../config';
import { createApiClient } from '../services/api-client';
import { createAuthService } from '../services/cognito';
import { thumbnailCache } from '../services/thumbnail-cache';
import { AuthContext, type AuthContextValue } from './auth-context';

export interface AuthProviderProps {
  config: AppConfig;
  children: ReactNode;
}

export const AuthProvider = ({ config, children }: AuthProviderProps) => {
  const auth = useMemo(() => createAuthService(config), [config]);
  const api = useMemo(
    () => createApiClient({ baseUrl: config.api_url, getIdToken: auth.getIdToken }),
    [config.api_url, auth]
  );

  // Seeded from the pool's stored session so a reload does not bounce a signed-in user to the login page.
  const [email, setEmail] = useState<string | undefined>(() => auth.currentUserEmail());

  const confirmSignedIn = useCallback(() => setEmail(auth.currentUserEmail()), [auth]);
  const signOut = useCallback(() => {
    auth.signOut();
    // The next user of this browser tab must not see the screens of this one's apps.
    thumbnailCache.clear();
    setEmail(undefined);
  }, [auth]);

  const value = useMemo<AuthContextValue>(
    () => ({ api, email, signedIn: email !== undefined, signIn: auth.signIn, confirmSignedIn, signOut }),
    [api, auth, email, confirmSignedIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
